import { Injectable } from '@angular/core';
import {
  AuthenticatorKey,
  IdxStatus,
  IdxTransaction,
  OktaAuth,
  Tokens
} from '@okta/okta-auth-js';
import { OktaService } from 'sustainment-component';

export interface RegistrationProfile {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  referralCode?: string;
}

export type RegistrationResult =
  | { status: 'verify-email' }
  | { status: 'complete'; tokens?: Tokens }
  | { status: 'error'; messages: string[] };

/**
 * Drives OIE self-service registration headlessly via the IDX API, replacing
 * the Classic widget's registration view. Field order, ToS gating and email
 * validation are owned by the signup form; this service owns the Okta side.
 *
 * Error contract:
 *   - Terminal business failures (duplicate email, policy rejection, etc.)
 *     resolve to `{ status: 'error', messages }` — inspect the status field.
 *   - Transport errors and unexpected IDX failures REJECT the returned promise;
 *     callers MUST attach a `.catch()` (or use try/catch) in addition to
 *     handling the `'error'` status.
 */
@Injectable()
export class RegistrationService {
  public constructor(private _oktaService: OktaService) {}

  /**
   * The widget's auth client, widened to OktaAuth: the signin-widget types
   * don't surface the `idx` API even though the runtime client carries it.
   */
  private get _authClient(): OktaAuth {
    return this._oktaService.widget.authClient as unknown as OktaAuth;
  }

  /**
   * Dedicated client for the enrollment ceremony. We must NOT drive
   * `idx.register()` on the widget's shared authClient: the Sign-In Widget owns
   * that client with its own configured (sign-in) flow, and a `register`-flow
   * transaction written into it makes the widget throw
   * `Invalid "flow" configuration`. A separate instance (same issuer/clientId)
   * keeps registration's flow state isolated from the widget.
   */
  private _regClient?: OktaAuth;
  private get _registerClient(): OktaAuth {
    if (this._regClient) return this._regClient;
    const opts = this._authClient.options;
    this._regClient = new OktaAuth({
      issuer: opts.issuer,
      clientId: opts.clientId,
      redirectUri: opts.redirectUri,
      scopes: opts.scopes,
      pkce: true
    });
    return this._regClient;
  }

  public async register(profile: RegistrationProfile): Promise<RegistrationResult> {
    // Start a clean enrollment flow on the isolated client.
    this._registerClient.idx.clearTransactionMeta();

    try {
      let transaction: IdxTransaction = await this._registerClient.idx.register({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        ...(profile.referralCode ? { referralCode: profile.referralCode } : {})
      });

      // This org collects the password inline on the enroll-profile form: the
      // remediation exposes a required `passcode` input alongside the profile
      // fields. idx.register(), called with profile fields only, leaves
      // enroll-profile UNSUBMITTED (PENDING) and no account is created. Submit
      // the form including the password so the account is actually created.
      transaction = await this.submitEnrollProfile(transaction, profile);

      transaction = await this.enrollPassword(transaction, profile.password);

      return this.toResult(transaction);
    } finally {
      // Clear the enrollment transaction from (shared) browser storage so the
      // widget's sign-in client doesn't later resume a register/enrollProfile
      // flow and throw `Invalid "flow" configuration` during post-register login.
      // Tokens are already captured in the returned result before this runs.
      this._registerClient.idx.clearTransactionMeta();
    }
  }

  /**
   * Submit the enroll-profile remediation when the org collects the password
   * inline on that form (a required `passcode` input alongside the profile
   * fields). idx.register() is called with profile fields only, so it returns
   * enroll-profile unsubmitted; resubmitting with the password creates the
   * account. No-op when enroll-profile is not the pending step (orgs that take
   * the password as a separate authenticator step have already advanced past
   * it), so this is safe for both org configurations.
   */
  private async submitEnrollProfile(
    transaction: IdxTransaction,
    profile: RegistrationProfile
  ): Promise<IdxTransaction> {
    if (
      transaction.status !== IdxStatus.PENDING ||
      transaction.nextStep?.name !== 'enroll-profile'
    ) {
      return transaction;
    }

    return this._registerClient.idx.proceed({
      firstName: profile.firstName,
      lastName: profile.lastName,
      email: profile.email,
      password: profile.password
    });
  }

  private async enrollPassword(
    transaction: IdxTransaction,
    password: string
  ): Promise<IdxTransaction> {
    // Walk known remediation steps; stop on anything else (e.g. email
    // verification, which the user completes from their inbox).
    for (let guard = 0; guard < 5 && transaction.status === IdxStatus.PENDING; guard++) {
      const step = transaction.nextStep?.name;
      const authenticator = transaction.nextStep?.authenticator?.key;

      if (step === 'select-authenticator-enroll') {
        if (this.isPasswordOption(transaction)) {
          // The org's enrollment policy still wants a password.
          transaction = await this._registerClient.idx.proceed({
            authenticator: AuthenticatorKey.OKTA_PASSWORD
          });
        } else if (this.canSkip(transaction)) {
          // Password (and any required factors) already enrolled; this select
          // step only offers OPTIONAL authenticators (e.g. Okta Verify) and the
          // org allows skipping them. Skip rather than fail the registration.
          transaction = await this._registerClient.idx.proceed({ step: 'skip' });
        } else {
          break;
        }
      } else if (
        step === 'enroll-authenticator' &&
        authenticator === AuthenticatorKey.OKTA_PASSWORD
      ) {
        transaction = await this._registerClient.idx.proceed({ password });
      } else {
        break;
      }
    }

    return transaction;
  }

  /**
   * Whether the current `select-authenticator-enroll` step offers password.
   *
   * okta-auth-js surfaces the selectable authenticators as the `authenticator`
   * input's `options` array, each `{ label, value }` where `value` is the
   * authenticator key string (e.g. 'okta_password', 'okta_verify'). When no
   * options are present (unknown shape, e.g. legacy test mocks) we conservatively
   * treat password as offered so the initial password-enrollment step is taken.
   */
  private isPasswordOption(transaction: IdxTransaction): boolean {
    const options = transaction.nextStep?.inputs?.find((i) => i.name === 'authenticator')?.options;
    if (!options) return true;
    return options.some((o) => o.value === AuthenticatorKey.OKTA_PASSWORD);
  }

  /**
   * Whether the org exposes a `skip` remediation at the current step, letting us
   * advance past an OPTIONAL authenticator enrollment without enrolling it.
   */
  private canSkip(transaction: IdxTransaction): boolean {
    return (
      transaction.nextStep?.canSkip === true ||
      (transaction.availableSteps || []).some((s) => s.name === 'skip')
    );
  }

  private toResult(transaction: IdxTransaction): RegistrationResult {
    if (transaction.status === IdxStatus.SUCCESS) {
      // Hand the freshly minted tokens back so the caller can establish the
      // session and continue straight into onboarding (company details),
      // rather than bouncing the new user to the login screen.
      return { status: 'complete', tokens: transaction.tokens };
    }

    const step = transaction.nextStep?.name;
    const authenticator = transaction.nextStep?.authenticator?.key;

    if (step === 'enroll-authenticator' && authenticator === AuthenticatorKey.OKTA_EMAIL) {
      return { status: 'verify-email' };
    }

    const messages = [...new Set((transaction.messages || []).map((m) => m.message))];

    return {
      status: 'error',
      messages: messages.length ? messages : ['Registration failed. Please try again.']
    };
  }
}
